import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import Hls from 'hls.js';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Settings, 
  Wifi, 
  PictureInPicture, 
  Cast, 
  SkipBack, 
  SkipForward,
  Minimize,
  RotateCcw,
  RotateCw,
  Subtitles,
  MonitorSpeaker,
  Download,
  Share,
  Heart,
  MoreVertical
} from 'lucide-react';
import './CustomPlayer.css';

/**
 * Advanced Custom Player - Inspired by FourKPlayer
 * 
 * Features:
 * - HLS (.m3u8) adaptive streaming support
 * - Multiple video format support (.mp4, .webm, .mov, etc.)
 * - Quality selection and adaptive bitrate
 * - Modern UI with smooth controls
 * - TMDB integration for movie metadata
 * - Progressive loading with analytics
 * - Picture-in-picture support
 * - Fullscreen capabilities
 * - Volume control and seeking
 */

// Video format detection utility
const detectVideoFormat = (url) => {
  if (!url) {
    return { format: 'other', isValid: false, message: 'No stream URL provided', mimeType: '' };
  }
  
  const urlLower = url.toLowerCase();
  
  // HLS Detection (highest priority for streaming)
  if (urlLower.includes('.m3u8') || urlLower.includes('hls') || urlLower.includes('stream/')) {
    return { 
      format: 'hls', 
      isValid: true, 
       mimeType: 'application/x-mpegURL'
    };
  }
  
  // MP4 Detection (most compatible)
  if (urlLower.includes('.mp4') || urlLower.includes('mp4')) {
    return { 
      format: 'mp4', 
      isValid: true, 
      message: 'MP4 video file detected',
      mimeType: 'video/mp4'
    };
  }
  
  // WebM Detection
  if (urlLower.includes('.webm')) {
    return { 
      format: 'webm', 
      isValid: true, 
      message: 'WebM video file detected',
      mimeType: 'video/webm'
    };
  }
  
  // Other formats
  if (urlLower.includes('.mov')) {
    return { format: 'mov', isValid: true, message: 'MOV video file detected', mimeType: 'video/quicktime' };
  }
  
  return { format: 'other', isValid: true, message: 'Direct video file', mimeType: 'video/*' };
};

// Advanced Custom Player Component
const CustomPlayer = ({ 
  src, 
  title = 'Stream', 
  poster = null,
  tmdbData = null,
  autoplay = true,
  mediaId = null,
  mediaType = 'movie',
  fullscreen = false
}) => {
  // ⚡ PERFORMANCE: Conditional debug logging
  const DEBUG_PLAYER = process.env.REACT_APP_DEBUG_PLAYER === 'true';
  const debugLog = (message, ...args) => {
    if (DEBUG_PLAYER) console.log(message, ...args);
  };
  
  // Debug tmdbData prop changes (only in debug mode)
  useEffect(() => {
    debugLog('🎭 CustomPlayer: tmdbData prop changed:', tmdbData ? 'Available' : 'Null');
    if (tmdbData) {
      debugLog('🎬 CustomPlayer: Movie title:', tmdbData.title);
      debugLog('🖼️ CustomPlayer: Backdrop available:', !!(tmdbData.backdrop_url || tmdbData.backdrop_path));
    }
  }, [tmdbData]);

  // Fallback hero image for known movie IDs when TMDB data is not available
  const getFallbackHeroData = useCallback(() => {
    if (tmdbData) {
      return tmdbData;
    }
    
    // Known movie fallbacks for immediate display
    const fallbackMovies = {
      '1239193': {
        title: 'Deep Cover',
        backdrop_path: '/sNpoGjbV2a65HfZXCtTvf313cBT.jpg',
        poster_path: '/1vXTHTbSQJs9r2hp4Uk08XzKwPp.jpg',
        release_date: '2025-06-12',
        vote_average: 6.7,
        overview: 'Kat is an improv comedy teacher beginning to question if she has missed her shot at success. When an undercover cop offers her the role of a lifetime, she recruits two of her students to infiltrate London gangland by impersonating dangerous criminals.',
        genres: ['Action', 'Comedy', 'Crime'],
        runtime: 99,
        backdrop_url: 'https://image.tmdb.org/t/p/w1280/sNpoGjbV2a65HfZXCtTvf313cBT.jpg'
      }
    };
    
    return mediaId && fallbackMovies[mediaId] ? fallbackMovies[mediaId] : null;
  }, [tmdbData, mediaId]);

  // Memoize hero data to prevent excessive recalculations
  const heroData = useMemo(() => getFallbackHeroData(), [getFallbackHeroData]);

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false); // Start unmuted like FourKPlayer
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showControls, setShowControls] = useState(true); // Always start with controls visible
  const [controlsTimeout, setControlsTimeout] = useState(null);
  const [volume, setVolume] = useState(1);
  const [selectedQuality, setSelectedQuality] = useState('Auto');
  const [availableQualities, setAvailableQualities] = useState(['Auto']);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPiP, setIsPiP] = useState(false);
  const [buffered, setBuffered] = useState(0);
  const [bufferedPercent, setBufferedPercent] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [videoFormat, setVideoFormat] = useState(null);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  
  // Subtitle states
  const [subtitles, setSubtitles] = useState([]);
  const [currentSubtitle, setCurrentSubtitle] = useState(null);
  const [selectedSubtitleTrack, setSelectedSubtitleTrack] = useState('off');
  const [subtitleSettings, setSubtitleSettings] = useState({
    fontSize: 16,
    fontFamily: 'Arial',
    color: '#ffffff',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    position: 'bottom',
    outline: true,
    outlineColor: '#000000'
  });
  const [showSubtitleSettings, setShowSubtitleSettings] = useState(false);
  const [availableSubtitleTracks, setAvailableSubtitleTracks] = useState([
    { id: 'off', label: 'Off', language: 'off' }
  ]);

  // Sync volume slider visual progress
  useEffect(() => {
    const volumeSlider = document.querySelector('.volume-slider');
    if (volumeSlider) {
      const volumePercent = (isMuted ? 0 : volume) * 100;
      volumeSlider.style.setProperty('--volume-percent', `${volumePercent}%`);
    }
  }, [volume, isMuted]);

  // Auto-hide controls after inactivity
  useEffect(() => {
    const handleActivity = () => {
      setLastActivity(Date.now());
      setShowControls(true);
      
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
      
      const timeout = setTimeout(() => {
        if (isPlaying && !showSettings) {
          setShowControls(false);
        }
      }, 3000);
      
      setControlsTimeout(timeout);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleActivity);
      container.addEventListener('click', handleActivity);
      container.addEventListener('keydown', handleActivity);
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleActivity);
        container.removeEventListener('click', handleActivity);
        container.removeEventListener('keydown', handleActivity);
      }
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
    };
  }, [isPlaying, showSettings, controlsTimeout]);

  // Subtitle utilities
  const parseWebVTT = useCallback((content) => {
    const lines = content.split('\n');
    const subtitles = [];
    let currentSubtitle = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip WEBVTT header and empty lines
      if (line === 'WEBVTT' || line === '' || line.startsWith('NOTE')) {
        continue;
      }
      
      // Time format: 00:00:10.500 --> 00:00:13.250
      if (line.includes(' --> ')) {
        const [startTime, endTime] = line.split(' --> ');
        currentSubtitle = {
          start: parseVTTTime(startTime),
          end: parseVTTTime(endTime),
          text: ''
        };
      } else if (currentSubtitle && line !== '') {
        // Text content
        currentSubtitle.text += (currentSubtitle.text ? '\n' : '') + line;
      } else if (currentSubtitle && line === '') {
        // End of subtitle block
        subtitles.push(currentSubtitle);
        currentSubtitle = null;
      }
    }
    
    // Add last subtitle if exists
    if (currentSubtitle) {
      subtitles.push(currentSubtitle);
    }
    
    return subtitles;
  }, []);
  
  const parseVTTTime = (timeString) => {
    const parts = timeString.split(':');
    const seconds = parseFloat(parts[2]);
    const minutes = parseInt(parts[1]);
    const hours = parseInt(parts[0]);
    return hours * 3600 + minutes * 60 + seconds;
  };
  
  const parseSRT = useCallback((content) => {
    const blocks = content.trim().split('\n\n');
    const subtitles = [];
    
    blocks.forEach(block => {
      const lines = block.split('\n');
      if (lines.length >= 3) {
        const timeMatch = lines[1].match(/(\d{2}:\d{2}:\d{2},\d{3}) --> (\d{2}:\d{2}:\d{2},\d{3})/);
        if (timeMatch) {
          const start = parseSRTTime(timeMatch[1]);
          const end = parseSRTTime(timeMatch[2]);
          const text = lines.slice(2).join('\n');
          
          subtitles.push({ start, end, text });
        }
      }
    });
    
    return subtitles;
  }, []);
  
  const parseSRTTime = (timeString) => {
    const [time, ms] = timeString.split(',');
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds + parseFloat(ms) / 1000;
  };
  
  // Load subtitles from URL or TMDB
  const loadSubtitles = useCallback(async (subtitleUrl, language = 'en') => {
    try {
      debugLog('🎬 Loading subtitles:', subtitleUrl);
      const response = await fetch(subtitleUrl);
      const content = await response.text();
      
      let parsedSubtitles = [];
      if (subtitleUrl.includes('.vtt') || content.includes('WEBVTT')) {
        parsedSubtitles = parseWebVTT(content);
      } else if (subtitleUrl.includes('.srt')) {
        parsedSubtitles = parseSRT(content);
      }
      
      setSubtitles(parsedSubtitles);
      
      // Add to available tracks
      const trackId = `subtitle_${language}`;
      const newTrack = {
        id: trackId,
        label: `${language.toUpperCase()} Subtitles`,
        language: language
      };
      
      setAvailableSubtitleTracks(prev => {
        const exists = prev.find(track => track.id === trackId);
        if (!exists) {
          return [...prev, newTrack];
        }
        return prev;
      });
      
      // Auto-select if it's the first subtitle track
      if (selectedSubtitleTrack === 'off') {
        setSelectedSubtitleTrack(trackId);
      }
      
      debugLog('✅ Subtitles loaded:', parsedSubtitles.length, 'entries');
    } catch (error) {
      console.error('❌ Failed to load subtitles:', error); // Keep error logs
    }
  }, [parseWebVTT, parseSRT, selectedSubtitleTrack]);
  
  // Auto-load subtitles from TMDB data
  useEffect(() => {
    if (tmdbData && mediaId) {
      // Try to load subtitles from common sources
      const possibleSubtitleUrls = [
        // OpenSubtitles.org format
        `https://api.opensubtitles.org/api/v1/subtitles?tmdb_id=${mediaId}&languages=en`,
        // Local subtitle files (if available)
        `/subtitles/${mediaId}_en.vtt`,
        `/subtitles/${mediaId}_en.srt`
      ];
      
      // For now, we'll just set up the structure
      // In a real implementation, you'd fetch from subtitle APIs
      if (DEBUG_PLAYER) console.log('🎯 TMDB ID for subtitles:', mediaId);
    }
  }, [tmdbData, mediaId, loadSubtitles]);
  
  // Demo subtitles for testing
  useEffect(() => {
    if (src && tmdbData) {
      // Add demo subtitles for testing
      const demoSubtitles = [
        { start: 10, end: 15, text: "Welcome to the movie!" },
        { start: 20, end: 25, text: "This is a demo subtitle" },
        { start: 30, end: 35, text: "You can customize the appearance\nin the subtitle settings" },
        { start: 40, end: 45, text: "Press 'C' to toggle subtitles" },
        { start: 50, end: 55, text: "Enjoy watching! 🎬" }
      ];
      
      setSubtitles(demoSubtitles);
      
      // Add demo track
      const demoTrack = {
        id: 'demo_en',
        label: 'English (Demo)',
        language: 'en'
      };
      
      setAvailableSubtitleTracks(prev => {
        const exists = prev.find(track => track.id === 'demo_en');
        if (!exists) {
          return [...prev, demoTrack];
        }
        return prev;
      });
      
      if (DEBUG_PLAYER) console.log('📝 Demo subtitles loaded');
    }
  }, [src, tmdbData]);
  
  // Update current subtitle based on video time
  useEffect(() => {
    if (subtitles.length > 0 && selectedSubtitleTrack !== 'off') {
      const activeSubtitle = subtitles.find(
        sub => currentTime >= sub.start && currentTime <= sub.end
      );
      setCurrentSubtitle(activeSubtitle || null);
    } else {
      setCurrentSubtitle(null);
    }
  }, [currentTime, subtitles, selectedSubtitleTrack]);

  // Show loading when TMDB data is available but no stream yet, or when TMDB data changes
  useEffect(() => {
    if (heroData && !src) {
      if (DEBUG_PLAYER) console.log('🎭 Hero data received, showing loading screen');
      setIsLoading(true);
      setLoadingProgress(0);
    } else if (heroData && src && isLoading) {
      // If hero data arrives while already loading, ensure loading screen shows movie info
      if (DEBUG_PLAYER) console.log('🎭 Hero data received during loading, updating loading screen');
      setLoadingProgress(Math.max(loadingProgress, 10)); // Ensure some progress to show movie info
    } else if (heroData && heroData.title && src && !isLoading) {
      // If hero data arrives after loading is complete, show it briefly
      if (DEBUG_PLAYER) console.log('🎭 Hero data received after loading complete, showing movie info overlay');
      setIsLoading(true);
      setLoadingProgress(95);
      setTimeout(() => {
        setIsLoading(false);
        setLoadingProgress(100);
      }, 2000);
    }
  }, [heroData, src, isLoading, loadingProgress]);

  // Show movie loading screen when hero data changes (even if stream is already loaded)
  useEffect(() => {
    if (heroData && heroData.title) {
      if (DEBUG_PLAYER) {
        console.log('🎬 Hero movie data updated:', heroData.title);
        console.log('🎭 Current video state:', {
          paused: videoRef.current?.paused,
          readyState: videoRef.current?.readyState,
          currentTime: videoRef.current?.currentTime
        });
      }
      
      // If video is not playing yet and we have hero data, show enhanced loading
      const video = videoRef.current;
      if (video && (video.readyState < 3 || video.paused || video.currentTime === 0)) { 
        if (DEBUG_PLAYER) console.log('🎭 Showing enhanced loading with movie data');
        setIsLoading(true);
        setLoadingProgress(30); // Show progress to display movie info
        
        // Auto-hide after showing movie info
        setTimeout(() => {
          if (video && video.readyState >= 3) { // If video is ready
            if (DEBUG_PLAYER) console.log('🎭 Auto-hiding loading screen after showing movie data');
            setIsLoading(false);
            setLoadingProgress(100);
          }
        }, 2500); // Show movie info for 2.5 seconds
      } else {
        if (DEBUG_PLAYER) console.log('🎭 Video already playing, not showing loading screen');
      }
    }
  }, [heroData?.title, heroData?.backdrop_url]); // Simplified dependencies

  // Detect if URL is HLS (.m3u8) or direct video
  const isHLS = useCallback((url) => {
    const format = detectVideoFormat(url);
    return format.format === 'hls';
  }, []);

  // Initialize video player with advanced format detection
  useEffect(() => {
    if (!src || !videoRef.current) return;

    const video = videoRef.current;
    const format = detectVideoFormat(src);
    setVideoFormat(format);
    setError(null);
    
    // ✅ Smart proxy for M3U8 streams - only proxy if needed
    let proxiedSrc = src;
    if (format.format === 'hls' && src.includes('.m3u8')) {
      // Check if this URL needs proxying
      const shouldUseProxy = (url) => {
        try {
          const domain = new URL(url).hostname;
          // Only SpencerDevs and VidJoy don't need proxying - proxy all other sources
          const corsFreeDomains = [
            'spencerdevs.xyz',  // SpencerDevs can be accessed directly
            'vidjoy.pro'        // VidJoy can be accessed directly
          ];
          // 🚨 PRODUCTION DEBUG: Always log proxy decision
          const needsProxy = !corsFreeDomains.some(freeDomain => domain.includes(freeDomain));
          console.log(`🔍 PRODUCTION: Domain ${domain} needs proxy:`, needsProxy);
          // Return true if NOT in the CORS-free domains (meaning it needs proxy)
          return needsProxy;
        } catch (error) {
          console.error('Error parsing URL for proxy check:', error);
          // If URL parsing fails, default to using proxy for safety
          return true;
        }
      };
      
      if (shouldUseProxy(src)) {
        // Use proxy to bypass origin-based CORS blocking
        proxiedSrc = `/proxy?url=${encodeURIComponent(src)}`;
        // 🚨 PRODUCTION DEBUG: Always log proxy usage
        console.log(`🔗 PRODUCTION: Using proxy for M3U8:`, proxiedSrc);
        console.log(`🔗 PRODUCTION: Original URL:`, src);
      } else {
        // 🚨 PRODUCTION DEBUG: Always log direct access
        console.log(`🔗 PRODUCTION: Using direct access for M3U8:`, src);
        proxiedSrc = src;
        // Direct usage for CORS-friendly domains
        if (DEBUG_PLAYER) {
          console.log(`🎯 Direct M3U8 (CORS-friendly domain):`, src);
        }
      }
    }
    
    // Clear any existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    setIsLoading(true);
    setLoadingProgress(0);
    // ⚡ PERFORMANCE: Conditional logging for format info
    if (DEBUG_PLAYER) {
      console.log(`🎬 Loading ${format.format.toUpperCase()} stream:`, src);
      console.log(`📊 Format info:`, format.message);
      console.log(`🎭 Hero Data available:`, heroData ? 'Yes' : 'No');
      if (heroData) {
        console.log(`🖼️ Backdrop URL:`, heroData.backdrop_url || heroData.backdrop_path);
        console.log(`🎬 Title:`, heroData.title);
        console.log(`📅 Release:`, heroData.release_date);
        console.log(`⭐ Rating:`, heroData.vote_average);
      } else {
        console.log('⏳ Hero data not yet available, will show enhanced loading when received');
      }
    }

    if (format.format === 'hls') {
      // HLS streaming (adaptive bitrate)
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,  // ⚡ PERFORMANCE: Enable worker for better performance
          lowLatencyMode: false,
          backBufferLength: 30,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          manifestLoadingTimeOut: 5000,    // ⚡ PERFORMANCE: 20s → 5s
          levelLoadingTimeOut: 5000,       // ⚡ PERFORMANCE: 20s → 5s  
          fragLoadingTimeOut: 8000,        // ⚡ PERFORMANCE: 30s → 8s
          debug: false,
          // ✅ CRITICAL FIX: Transform ALL URLs (segments, manifests, etc.) to use proxy
          loader: class extends Hls.DefaultConfig.loader {
            load(context, config, callbacks) {
              let originalUrl = context.url;
              
              // 🚨 CRITICAL: Don't proxy URLs that are already proxied!
              if (originalUrl.startsWith('/proxy?url=') || originalUrl.includes('/proxy?url=')) {
                console.log(`🔄 HLS Already Proxied: ${originalUrl}`);
                super.load(context, config, callbacks);
                return;
              }
              
              // Check if this URL needs to go through proxy
              const shouldProxyUrl = (url) => {
                try {
                  const domain = new URL(url).hostname;
                  const corsFreeDomains = [
                    'spencerdevs.xyz',
                    'vidjoy.pro'
                  ];
                  return !corsFreeDomains.some(freeDomain => domain.includes(freeDomain));
                } catch (error) {
                  return true; // Default to proxy if URL parsing fails
                }
              };
              
              // Transform URL to use proxy if needed
              if (shouldProxyUrl(originalUrl)) {
                context.url = `/proxy?url=${encodeURIComponent(originalUrl)}`;
                console.log(`🔗 HLS Proxy: ${originalUrl} → ${context.url}`);
              } else {
                console.log(`🎯 HLS Direct: ${originalUrl}`);
              }
              
              // Call parent loader with the modified URL
              super.load(context, config, callbacks);
            }
          },
          // Improved fragment loading
          maxLoadingDelay: 4,
          maxBufferHole: 0.5,
          highBufferWatchdogPeriod: 2,
          nudgeOffset: 0.1,
          nudgeMaxRetry: 3,
          manifestLoadingMaxRetry: 3,
          levelLoadingMaxRetry: 3,
          fragLoadingMaxRetry: 3
        });
        
        hls.loadSource(proxiedSrc);  // Use proxy to bypass origin-based CORS blocking
        hls.attachMedia(video);
        hlsRef.current = hls;

        // HLS Events
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (DEBUG_PLAYER) console.log('✅ HLS manifest loaded successfully');
          
          // Only show hero data loading if autoplay is disabled
          if (!autoplay && heroData && heroData.title) {
            if (DEBUG_PLAYER) console.log('🎭 Keeping loading screen to show movie data (no autoplay)');
            setLoadingProgress(90); // Almost complete but keep loading visible
            // Hide loading after showing movie info for a moment
            setTimeout(() => {
              setIsLoading(false);
              setLoadingProgress(100);
            }, 2000); // Show movie info for 2 seconds
          } else {
            // Hide loading immediately if autoplay is enabled (play event will handle the rest)
            setIsLoading(false);
            setLoadingProgress(100);
          }
          
          // Extract available qualities
          const levels = hls.levels.map((level, index) => ({
            index,
            height: level.height,
            width: level.width,
            bitrate: level.bitrate,
            label: level.height ? `${level.height}p` : `Level ${index + 1}`
          }));
          
          const qualityLabels = ['Auto', ...levels.map(l => l.label)];
          setAvailableQualities(qualityLabels);
          
          if (DEBUG_PLAYER) console.log('🎯 Available qualities:', qualityLabels);
          
          if (autoplay) {
            // FourKPlayer approach - check if paused first
            if (DEBUG_PLAYER) console.log('🔊 Attempting autoplay with sound (source selection method)');
            const attemptPlay = async () => {
              if (video && video.paused) {
                try {
                  await video.play();
                  if (DEBUG_PLAYER) console.log('✅ Autoplay successful!');
                  setIsPlaying(true);
                  // Hide loading screen immediately when autoplay succeeds
                  setIsLoading(false);
                  setLoadingProgress(100);
                } catch (e) {
                  if (DEBUG_PLAYER) console.log('⚠️ Autoplay failed, retrying...', e.message);
                  // Retry after a short delay
                  setTimeout(async () => {
                    if (video && video.paused) {
                      try {
                        await video.play();
                        if (DEBUG_PLAYER) console.log('✅ Autoplay retry successful!');
                        setIsPlaying(true);
                        // Hide loading screen immediately when autoplay retry succeeds
                        setIsLoading(false);
                        setLoadingProgress(100);
                      } catch (retryError) {
                        if (DEBUG_PLAYER) console.log('❌ Autoplay retry failed:', retryError.message);
                      }
                    }
                  }, 500);
                }
              } else {
                if (DEBUG_PLAYER) console.log('🎬 Video already playing or not ready');
              }
            };
            attemptPlay();
          }
        });

        hls.on(Hls.Events.FRAG_LOADING, () => {
          setLoadingProgress(prev => Math.min(prev + 10, 90));
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('❌ HLS Error:', data);
          
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                if (DEBUG_PLAYER) console.log('Fatal network error encountered, trying to recover...');
                if (data.details === Hls.ErrorDetails.FRAG_LOAD_ERROR) {
                  // Try direct video fallback for fragment errors
                  if (DEBUG_PLAYER) console.log('Fragment load failed, trying direct video...');
                  setError('Fragment loading failed. Trying direct playback...');
                  video.src = proxiedSrc;
                  video.load();
                } else {
                  setError('Network error loading stream. Please check your connection.');
                  hls.startLoad();
                }
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                setError('Media error. Trying to recover...');
                hls.recoverMediaError();
                break;
              default:
                setError('Fatal error loading stream.');
                setIsLoading(false);
                // Fallback to direct video
                if (DEBUG_PLAYER) console.log('Trying direct video fallback...');
                video.src = proxiedSrc;
                video.load();
                break;
            }
          }
        });

        hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
          const level = hls.levels[data.level];
          if (level && level.height) {
            if (DEBUG_PLAYER) console.log(`🔄 Quality switched to: ${level.height}p`);
          }
        });

      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        if (DEBUG_PLAYER) console.log('🍎 Using native HLS support (Safari)');
        video.src = proxiedSrc;
        setIsLoading(false);
        if (autoplay) {
          // Aggressive autoplay with multiple attempts
          if (DEBUG_PLAYER) console.log('🔊 Attempting autoplay with sound (Safari HLS)');
          const attemptPlay = async () => {
            try {
              await video.play();
              if (DEBUG_PLAYER) console.log('✅ Safari autoplay successful!');
              setIsPlaying(true);
              // Hide loading screen immediately when Safari autoplay succeeds
              setIsLoading(false);
              setLoadingProgress(100);
            } catch (e) {
              if (DEBUG_PLAYER) console.log('⚠️ Safari autoplay failed, retrying...', e.message);
              setTimeout(async () => {
                try {
                  await video.play();
                  if (DEBUG_PLAYER) console.log('✅ Safari autoplay retry successful!');
                  setIsPlaying(true);
                  // Hide loading screen immediately when Safari autoplay retry succeeds
                  setIsLoading(false);
                  setLoadingProgress(100);
                } catch (retryError) {
                  if (DEBUG_PLAYER) console.log('❌ Safari autoplay retry failed:', retryError.message);
                }
              }, 500);
            }
          };
          attemptPlay();
        }
      } else {
        setError('HLS not supported in this browser');
        setIsLoading(false);
      }
    } else {
      // Direct video file
      if (DEBUG_PLAYER) console.log(`📹 Loading direct ${format.format.toUpperCase()} file`);
      video.src = src;
      
      // Check if browser supports this format
      const canPlay = video.canPlayType(format.mimeType);
      if (canPlay === '') {
        setError(`${format.format.toUpperCase()} format not supported in this browser`);
      }
      
      setIsLoading(false);
      if (autoplay) {
        // Aggressive autoplay with multiple attempts
        if (DEBUG_PLAYER) console.log('🔊 Attempting autoplay with sound (direct video)');
        const attemptPlay = async () => {
          try {
            await video.play();
            if (DEBUG_PLAYER) console.log('✅ Direct video autoplay successful!');
            setIsPlaying(true);
            // Hide loading screen immediately when direct video autoplay succeeds
            setIsLoading(false);
            setLoadingProgress(100);
          } catch (e) {
            if (DEBUG_PLAYER) console.log('⚠️ Direct video autoplay failed, retrying...', e.message);
            setTimeout(async () => {
              try {
                await video.play();
                if (DEBUG_PLAYER) console.log('✅ Direct video autoplay retry successful!');
                setIsPlaying(true);
                // Hide loading screen immediately when direct video autoplay retry succeeds
                setIsLoading(false);
                setLoadingProgress(100);
              } catch (retryError) {
                if (DEBUG_PLAYER) console.log('❌ Direct video autoplay retry failed:', retryError.message);
              }
            }, 500);
          }
        };
        attemptPlay();
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, autoplay, isHLS, heroData]); // Added heroData dependency

  // Enhanced video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
      setError(null);
      // Hide loading screen immediately when video starts playing
      setIsLoading(false);
      setLoadingProgress(100);
      if (DEBUG_PLAYER) console.log('🎬 Video started playing, hiding loading screen');
    };
    
    const handlePause = () => setIsPlaying(false);
    
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      
      // Update buffered progress
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const bufferedPercentValue = (bufferedEnd / video.duration) * 100;
        setBuffered(bufferedPercentValue);
        setBufferedPercent(bufferedPercentValue);
      }
    };
    
    const handleDurationChange = () => setDuration(video.duration);
    
    const handleVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
      setLoadingProgress(0);
    };

    const handleCanPlay = () => {
      // Only show hero data loading if autoplay is disabled
      if (!autoplay && heroData && heroData.title) {
        if (DEBUG_PLAYER) console.log('🎭 Video ready but keeping loading screen to show movie data (no autoplay)');
        setLoadingProgress(95);
        // Hide loading after showing movie info
        setTimeout(() => {
          setIsLoading(false);
          setLoadingProgress(100);
        }, 1500);
      } else {
        // Hide loading immediately if autoplay is enabled (play event will handle the rest)
        setIsLoading(false);
        setLoadingProgress(100);
      }
    };

    const handleError = (e) => {
      console.error('❌ Video error:', e);
      setError('Failed to load video. Please try again.');
      setIsLoading(false);
    };

    const handleRateChange = () => {
      setPlaybackRate(video.playbackRate);
    };

    // Add all event listeners
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('volumechange', handleVolumeChange);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.addEventListener('ratechange', handleRateChange);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('volumechange', handleVolumeChange);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      video.removeEventListener('ratechange', handleRateChange);
    };
  }, [heroData, autoplay]); // Added autoplay dependency

  // Advanced control functions
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    if (error) {
      // Try to reload if there was an error
      setError(null);
      video.load();
      return;
    }
    
    if (isPlaying) {
      video.pause();
    } else {
      video.play().then(() => {
        // Hide loading screen immediately when manual play succeeds
        setIsLoading(false);
        setLoadingProgress(100);
      }).catch(e => {
        console.error('Play failed:', e);
        setError('Failed to play video. Please try again.');
      });
    }
  }, [isPlaying, error]);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = !video.muted;
  }, []);

  const handleVolumeChange = useCallback((e) => {
    const video = videoRef.current;
    if (!video) return;
    
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    
    // Update volume slider visual progress
    const volumeSlider = e.target;
    const volumePercent = newVolume * 100;
    volumeSlider.style.setProperty('--volume-percent', `${volumePercent}%`);
    
    if (newVolume === 0) {
      video.muted = true;
    } else if (video.muted) {
      video.muted = false;
    }
  }, []);

  const handleSeek = useCallback((e) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    
    // Handle range input change
    if (e.target.type === 'range') {
      const percent = parseFloat(e.target.value) / 100;
      const seekTime = percent * duration;
      video.currentTime = Math.max(0, Math.min(seekTime, duration));
    } else {
      // Handle direct click on progress bar (legacy support)
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const seekTime = percent * duration;
      video.currentTime = Math.max(0, Math.min(seekTime, duration));
    }
  }, [duration]);

  const skipTime = useCallback((seconds) => {
    const video = videoRef.current;
    if (!video) return;
    
    video.currentTime = Math.max(0, Math.min(video.currentTime + seconds, duration));
  }, [duration]);

  const changeQuality = useCallback((quality) => {
    if (!hlsRef.current) return;
    
    setSelectedQuality(quality);
    
    if (quality === 'Auto') {
      hlsRef.current.currentLevel = -1; // Auto quality
      if (DEBUG_PLAYER) console.log('🔄 Quality set to Auto');
    } else {
      const qualityIndex = availableQualities.indexOf(quality) - 1; // -1 for 'Auto'
      if (qualityIndex >= 0 && qualityIndex < hlsRef.current.levels.length) {
        hlsRef.current.currentLevel = qualityIndex;
        if (DEBUG_PLAYER) console.log(`🔄 Quality manually set to: ${quality}`);
      }
    }
  }, [availableQualities]);

  const changePlaybackRate = useCallback((rate) => {
    const video = videoRef.current;
    if (!video) return;
    
    video.playbackRate = rate;
    setPlaybackRate(rate);
    if (DEBUG_PLAYER) console.log(`⚡ Playback rate set to: ${rate}x`);
  }, []);
  
  const changeSubtitleTrack = useCallback((trackId) => {
    setSelectedSubtitleTrack(trackId);
    if (DEBUG_PLAYER) console.log(`📝 Subtitle track changed to: ${trackId}`);
    
    if (trackId === 'off') {
      setCurrentSubtitle(null);
    }
  }, []);
  
  const updateSubtitleSettings = useCallback((newSettings) => {
    setSubtitleSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      container.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(e => {
        console.error('Fullscreen failed:', e);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  }, []);

  const togglePiP = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !document.pictureInPictureEnabled) return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiP(false);
      } else {
        await video.requestPictureInPicture();
        setIsPiP(true);
      }
    } catch (e) {
      console.error('Picture-in-Picture failed:', e);
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!containerRef.current || document.activeElement?.tagName === 'INPUT') return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skipTime(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skipTime(10);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume(prev => Math.min(prev + 0.1, 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume(prev => Math.max(prev - 0.1, 0));
          break;
        case 'KeyM':
          e.preventDefault();
          toggleMute();
          break;
        case 'KeyF':
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'KeyC':
          e.preventDefault();
          // Toggle subtitles
          const currentIndex = availableSubtitleTracks.findIndex(track => track.id === selectedSubtitleTrack);
          const nextIndex = (currentIndex + 1) % availableSubtitleTracks.length;
          changeSubtitleTrack(availableSubtitleTracks[nextIndex].id);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, skipTime, toggleMute, toggleFullscreen, changeSubtitleTrack, availableSubtitleTracks, selectedSubtitleTrack]);

  const formatTime = useCallback((time) => {
    if (!time || isNaN(time)) return '0:00';
    
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  const formatBytes = useCallback((bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }, []);
  
  // Function to manually load subtitles from URL
  const loadExternalSubtitles = useCallback(async (url, language = 'en') => {
    if (!url) return;
    
    try {
      await loadSubtitles(url, language);
    } catch (error) {
      console.error('Failed to load external subtitles:', error);
    }
  }, [loadSubtitles]);
  
  // Expose subtitle loading function for external use
  useEffect(() => {
    // Make subtitle loading available to parent component
    if (window.playerSubtitleLoader) {
      window.playerSubtitleLoader = loadExternalSubtitles;
    }
  }, [loadExternalSubtitles]);

  return (
    <div ref={containerRef} className={`fourkplayer-container ${fullscreen ? 'fullscreen-mode' : ''}`} tabIndex={0}>
      {/* Movie Info Header */}
      {heroData && !fullscreen && (
        <div className="player-header">
          <div className="movie-info">
            <h2 className="movie-title">{heroData.title || title}</h2>
            <div className="movie-details">
              {heroData.release_date && (
                <span className="year">{new Date(heroData.release_date).getFullYear()}</span>
              )}
              {heroData.genres && heroData.genres.length > 0 && (
                <span className="genres">{heroData.genres.slice(0, 2).join(', ')}</span>
              )}
              {heroData.vote_average && (
                <span className="rating">⭐ {heroData.vote_average.toFixed(1)}</span>
              )}
              {videoFormat && (
                <span className="format-badge">{videoFormat.format.toUpperCase()}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video Container */}
      <div className="video-container">
        {/* Background Image */}
        {heroData?.backdrop_url && (
          <div 
            className="video-background"
            style={{ backgroundImage: `url(${heroData.backdrop_url})` }}
          />
        )}
        
        {/* Video Element */}
        <video
          ref={videoRef}
          className="video-element"
          poster={poster || heroData?.backdrop_url}
          playsInline
          preload="metadata"
          crossOrigin="anonymous"
          onClick={togglePlay}
        />

        {/* Big Play/Pause Button - Always show in center */}
        {showControls && (
          <button className="big-play-button" onClick={togglePlay}>
            {isPlaying ? <Pause size={32} /> : <Play size={32} />}
          </button>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div className="loading-overlay">
            {/* Hero Background Image */}
            {heroData?.backdrop_url && (
              <div 
                className="loading-hero-bg"
                style={{ 
                  backgroundImage: `url(${heroData.backdrop_url})`,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: 0.4,
                  zIndex: 1
                }}
              />
            )}
            {/* Fallback backdrop from hero data */}
            {!heroData?.backdrop_url && heroData?.backdrop_path && (
              <div 
                className="loading-hero-bg"
                style={{ 
                  backgroundImage: `url(https://image.tmdb.org/t/p/w1280${heroData.backdrop_path})`,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: 0.4,
                  zIndex: 1
                }}
              />
            )}
            <div className="loading-content" style={{ zIndex: 2, position: 'relative' }}>
              {/* Movie Title */}
              {heroData?.title && (
                <h2 className="loading-movie-title" style={{
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  color: '#fff',
                  textAlign: 'center',
                  marginBottom: '1rem',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                }}>
                  {heroData.title}
                </h2>
              )}
              
              {/* Movie Details */}
              {heroData && (
                <div className="loading-movie-details" style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '1rem',
                  marginBottom: '2rem',
                  fontSize: '0.9rem',
                  color: '#ccc',
                  flexWrap: 'wrap'
                }}>
                  {heroData.release_date && (
                    <span>{new Date(heroData.release_date).getFullYear()}</span>
                  )}
                  {heroData.vote_average && (
                    <span>⭐ {heroData.vote_average.toFixed(1)}</span>
                  )}
                  {heroData.runtime && (
                    <span>{heroData.runtime} min</span>
                  )}
                  {heroData.genres && heroData.genres.length > 0 && (
                    <span>{heroData.genres.slice(0, 2).map(g => g.name || g).join(', ')}</span>
                  )}
                </div>
              )}

              <div className="loading-spinner"></div>
              <p className="loading-text">
                {loadingProgress > 0 ? `Loading ${loadingProgress}%...` : 'Initializing player...'}
              </p>
              {videoFormat && (
                <p className="loading-format">{videoFormat.message}</p>
              )}
              <div className="loading-progress">
                <div className="loading-progress-bar" style={{ width: `${loadingProgress}%` }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Error Overlay */}
        {error && (
          <div className="error-overlay">
            <div className="error-content">
              <div className="error-icon">⚠️</div>
              <h3>Playback Error</h3>
              <p>{error}</p>
              <button onClick={togglePlay} className="retry-button">
                <RotateCcw size={20} style={{ marginRight: '8px' }} />
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Controls Overlay */}
        {showControls && !isLoading && !error && (
          <div className="controls-overlay">
            {/* Top Controls */}
            <div className="top-controls">
              <div className="player-info">
                {playbackRate !== 1 && (
                  <span className="playback-rate">{playbackRate}x</span>
                )}
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="settings-panel">
                <div className="settings-header">
                  <h4>Player Settings</h4>
                  <button 
                    onClick={() => setShowSettings(false)} 
                    className="close-settings-btn"
                    title="Close Settings"
                  >
                    ×
                  </button>
                </div>
                
                <div className="settings-content">
                  {/* Quality Section */}
                  <div className="settings-section">
                    <label className="settings-label">
                      <Settings size={16} />
                      Video Quality
                    </label>
                    <div className="quality-grid">
                      {availableQualities.map(quality => (
                        <button
                          key={quality}
                          onClick={() => changeQuality(quality)}
                          className={`quality-btn ${selectedQuality === quality ? 'active' : ''}`}
                        >
                          {quality}
                          {selectedQuality === quality && <span className="checkmark">✓</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Speed Section */}
                  <div className="settings-section">
                    <label className="settings-label">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        ⚡ Playback Speed
                      </div>
                    </label>
                    <div className="speed-grid">
                      {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map(rate => (
                        <button
                          key={rate}
                          onClick={() => changePlaybackRate(rate)}
                          className={`speed-btn ${playbackRate === rate ? 'active' : ''}`}
                        >
                          {rate}×
                          {playbackRate === rate && <span className="checkmark">✓</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Subtitle Settings Panel */}
            {showSubtitleSettings && (
              <div className="subtitle-settings-panel">
                <h4>Subtitle Settings</h4>
                
                <div className="settings-section">
                  <label>Track:</label>
                  <select
                    value={selectedSubtitleTrack}
                    onChange={(e) => changeSubtitleTrack(e.target.value)}
                    className="subtitle-track-select"
                  >
                    {availableSubtitleTracks.map(track => (
                      <option key={track.id} value={track.id}>
                        {track.label}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedSubtitleTrack !== 'off' && (
                  <>
                    <div className="settings-section">
                      <label>Font Size:</label>
                      <input
                        type="range"
                        min="12"
                        max="32"
                        value={subtitleSettings.fontSize}
                        onChange={(e) => updateSubtitleSettings({ fontSize: parseInt(e.target.value) })}
                        className="subtitle-slider"
                      />
                      <span className="setting-value">{subtitleSettings.fontSize}px</span>
                    </div>

                    <div className="settings-section">
                      <label>Font Family:</label>
                      <select
                        value={subtitleSettings.fontFamily}
                        onChange={(e) => updateSubtitleSettings({ fontFamily: e.target.value })}
                        className="font-select"
                      >
                        <option value="Arial">Arial</option>
                        <option value="Helvetica">Helvetica</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Courier New">Courier New</option>
                        <option value="Verdana">Verdana</option>
                      </select>
                    </div>

                    <div className="settings-section">
                      <label>Text Color:</label>
                      <div className="color-options">
                        {['#ffffff', '#ffff00', '#ff0000', '#00ff00', '#00ffff', '#ff00ff'].map(color => (
                          <button
                            key={color}
                            className={`color-btn ${subtitleSettings.color === color ? 'active' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => updateSubtitleSettings({ color })}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="settings-section">
                      <label>Background:</label>
                      <div className="bg-options">
                        { [
                          { label: 'Black', value: 'rgba(0, 0, 0, 0.8)' },
                          { label: 'Dark', value: 'rgba(0, 0, 0, 0.5)' },
                          { label: 'None', value: 'transparent' }
                        ].map(bg => (
                          <button
                            key={bg.value}
                            className={`bg-btn ${subtitleSettings.backgroundColor === bg.value ? 'active' : ''}`}
                            onClick={() => updateSubtitleSettings({ backgroundColor: bg.value })}
                          >
                            {bg.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="settings-section">
                      <label>Position:</label>
                      <div className="position-buttons">
                        {['top', 'bottom'].map(pos => (
                          <button
                            key={pos}
                            className={`position-btn ${subtitleSettings.position === pos ? 'active' : ''}`}
                            onClick={() => updateSubtitleSettings({ position: pos })}
                          >
                            {pos.charAt(0).toUpperCase() + pos.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="settings-section">
                      <label>
                        <input
                          type="checkbox"
                          checked={subtitleSettings.outline}
                          onChange={(e) => updateSubtitleSettings({ outline: e.target.checked })}
                        />
                        Text Outline
                      </label>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Bottom Controls - Modern Netflix Style */}
            <div className="bottom-controls">
              {/* Enhanced Progress Bar with Buffer/Watched Colors - FourK Style */}
              <div className="progress-bar-container">
                <div className="progress-track" style={{ height: '6px', width: '96%', borderRadius: '3px', position: 'relative', marginBottom: '0px' }}>
                  {/* Buffer/Loaded portion (slightly lighter) */}
                  <div 
                    className="progress-buffer"
                    style={{ width: `${bufferedPercent || 0}%`, height: '6px', borderRadius: '3px', position: 'absolute', left: 0, top: 0 }}
                  />
                  {/* Watched portion (golden gradient) */}
                  <div 
                    className="progress-watched"
                    style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%`, height: '6px', borderRadius: '3px', position: 'absolute', left: 0, top: 0, background: 'linear-gradient(to right, #fbbf24, #f59e0b)' }}
                  />
                </div>
                {/* Invisible range input for interaction */}
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={duration ? (currentTime / duration) * 100 : 0}
                  onChange={handleSeek}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '96%',
                    height: '6px',
                    background: 'transparent',
                    outline: 'none',
                    zIndex: 10,
                    cursor: 'pointer',
                  }}
                />
                {/* Custom thumb/handle */}
                <div 
                  className="progress-thumb"
                  style={{ 
                    position: 'absolute',
                    top: '50%', // Center vertically on the progress bar
                    transform: 'translate(-50%, -50%)', // Center horizontally and vertically
                    left: duration && currentTime > 0
                      ? `calc(${(currentTime / duration) * 100}% )`
                      : '0px', // Absolutely flush to the start
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                    boxShadow: '0 0 2px #fbbf24',
                    border: '1.5px solid #fff',
                    opacity: showControls ? 1 : 0,
                    transition: 'opacity 0.2s',
                    zIndex: 11
                  }}
                />
                {/* Time tooltip - shows current time */}
                <div 
                  className="progress-time-tooltip"
                  style={{ 
                    left: `calc(${duration ? (currentTime / duration) * 100 : 0}% - 20px)`
                  }}
                >
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>

              {/* Controls Row */}
              <div className="controls-row">
                <div className="left-controls">
                  <button onClick={togglePlay} className="control-btn play-pause-btn">
                    {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                  </button>
                  
                  <button onClick={() => skipTime(-10)} className="control-btn">
                    <SkipBack size={20} />
                  </button>
                  
                  <button onClick={() => skipTime(10)} className="control-btn">
                    <SkipForward size={20} />
                  </button>
                  
                  <div 
                    className="volume-container"
                    onMouseEnter={() => setShowVolumeSlider(true)}
                    onMouseLeave={() => setShowVolumeSlider(false)}
                  >
                    <button onClick={toggleMute} className="control-btn volume-btn">
                      {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                    {/* Custom volume bar background and fill */}
                    <div 
                      className={`volume-slider-container ${
                        showVolumeSlider ? 'volume-slider-visible' : 'volume-slider-hidden'
                      }`}
                      style={{ position: 'relative' }}
                    >
                      {/* Background bar */}
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 64,
                        height: 4,
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: 2,
                        zIndex: 0
                      }} />
                      {/* Filled bar */}
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: `${(isMuted ? 0 : volume) * 64}px`,
                        height: 4,
                        background: 'linear-gradient(to right, #fbbf24, #f59e0b)',
                        borderRadius: 2,
                        zIndex: 1
                      }} />
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={isMuted ? 0 : volume}
                        onChange={handleVolumeChange}
                        className="volume-slider"
                        style={{
                          position: 'relative',
                          zIndex: 2,
                          background: 'transparent',
                          width: 64,
                          height: 4,
                          cursor: 'pointer',
                        }}
                      />
                      <span className="volume-percentage" style={{ zIndex: 3 }}>
                        {isMuted ? 'MUTE' : `${Math.round(volume * 100)}%`}
                      </span>
                    </div>
                  </div>
                  
                  <span className="time-display">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
                
                <div className="right-controls">
                  <button 
                    onClick={() => setShowSettings(!showSettings)} 
                    className={`control-btn settings-btn ${showSettings ? 'active' : ''}`}
                    title="Settings"
                  >
                    <Settings size={20} />
                  </button>
                  
                  <button 
                    onClick={() => {
                      const currentIndex = availableSubtitleTracks.findIndex(track => track.id === selectedSubtitleTrack);
                      const nextIndex = (currentIndex + 1) % availableSubtitleTracks.length;
                      changeSubtitleTrack(availableSubtitleTracks[nextIndex].id);
                    }} 
                    className="control-btn"
                    title="Toggle Subtitles"
                  >
                    <Subtitles size={20} />
                  </button>
                  
                  {document.pictureInPictureEnabled && (
                    <button onClick={togglePiP} className="control-btn" title="Picture in Picture">
                      <PictureInPicture size={20} />
                    </button>
                  )}
                  
                  <button onClick={toggleFullscreen} className="control-btn" title="Fullscreen">
                    {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subtitle Display */}
        {currentSubtitle && selectedSubtitleTrack !== 'off' && (
          <div 
            className="subtitle-display"
            style={{
              fontSize: `${subtitleSettings.fontSize}px`,
              fontFamily: subtitleSettings.fontFamily,
              color: subtitleSettings.color,
              backgroundColor: subtitleSettings.backgroundColor,
              bottom: subtitleSettings.position === 'bottom' ? '80px' : 'auto',
              top: subtitleSettings.position === 'top' ? '20px' : 'auto',
              textShadow: subtitleSettings.outline ? `2px 2px 0 ${subtitleSettings.outlineColor}` : 'none'
            }}
          >
            {currentSubtitle.text.split('\n').map((line, index) => (
              <div key={index} className="subtitle-line">
                {line}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Movie Overview */}
      {(() => {
        const heroData = getFallbackHeroData();
        return heroData?.overview && (
          <div className="movie-overview">
            <h3>Overview</h3>
            <p>{heroData.overview}</p>
            
            {/* Additional Info */}
            <div className="movie-stats">
              {heroData.runtime && (
                <span className="stat">Runtime: {heroData.runtime} min</span>
              )}
              {heroData.original_language && (
                <span className="stat">Language: {heroData.original_language.toUpperCase()}</span>
              )}
              {videoFormat && (
                <span className="stat">Format: {videoFormat.format.toUpperCase()}</span>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CustomPlayer;
